package dk.smark.demo.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.graphics.withSave
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import demo.composeapp.generated.resources.Res
import demo.composeapp.generated.resources.marker
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import kotlin.math.*

@Composable
fun TileMapView(
    center: LatLon,
    zoom: Int = 15,
    markers: List<MapMarker> = emptyList(),
    polylines: List<MapPolyline> = emptyList(),
    commands: SharedFlow<MapCommand>? = null,
    modifier: Modifier = Modifier,
    onZoomChanged: (Int) -> Unit = {},
) {
    val tileSize = 256
    // Load the painter once in composable scope
    val markerPainter = painterResource(Res.drawable.marker)
    val markerSize = 36f // display size in pixels

    var panOffset by remember { mutableStateOf(Offset.Zero) }
    var currentZoom by remember { mutableIntStateOf(zoom) }
    var zoomScale by remember { mutableFloatStateOf(1f) } // continuous sub-integer zoom factor

    val scope = rememberCoroutineScope()
    val tiles = remember { mutableStateMapOf<Triple<Int, Int, Int>, androidx.compose.ui.graphics.ImageBitmap>() }
    val loadingTiles = remember { mutableSetOf<Triple<Int, Int, Int>>() } // ADD THIS

    // Listen to zoom commands from the controller
    LaunchedEffect(commands) {
        commands?.collect { command ->
            when (command) {
                is MapCommand.ZoomIn -> {
                    val newZoom = (currentZoom + 1).coerceIn(1, 19)
                    if (newZoom != currentZoom) {
                        panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                        currentZoom = newZoom
                        onZoomChanged(currentZoom)
                    }
                }

                is MapCommand.ZoomOut -> {
                    val newZoom = (currentZoom - 1).coerceIn(1, 19)
                    if (newZoom != currentZoom) {
                        panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                        currentZoom = newZoom
                        onZoomChanged(currentZoom)
                    }
                }

                else -> {}
            }
        }
    }

    // Track previous centroid and span for manual pinch detection
    var lastCentroid by remember { mutableStateOf<Offset?>(null) }
    var lastSpan by remember { mutableStateOf<Float?>(null) }

    Canvas(
        modifier = modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent(PointerEventPass.Main)

                        // Always consume all changes to prevent propagation
                        event.changes.forEach { it.consume() }

                        val pointers = event.changes.filter { it.pressed }

                        if (pointers.isEmpty()) {
                            lastCentroid = null
                            lastSpan = null
                            continue
                        }

                        if (pointers.size == 1) {
                            // Single finger pan
                            val current = pointers[0].position
                            val previous = pointers[0].previousPosition
                            val delta = current - previous
                            if (lastCentroid != null) {
                                panOffset += delta
                            }
                            lastCentroid = current
                            lastSpan = null
                        } else {
                            // Multi-finger pinch: use first two pointers
                            val p0 = pointers[0].position
                            val p1 = pointers[1].position
                            val centroid = (p0 + p1) / 2f
                            val span = (p0 - p1).getDistance()

                            val prevSpan = lastSpan
                            val prevCentroid = lastCentroid

                            if (prevSpan != null && prevCentroid != null && prevSpan > 0f) {
                                // Pan by centroid movement
                                val panDelta = centroid - prevCentroid
                                panOffset += panDelta

                                // Zoom toward the centroid
                                val spanRatio = span / prevSpan
                                val newScale = zoomScale * spanRatio

                                // Zoom toward pinch centroid: shift pan so the
                                // world point under the centroid stays fixed
                                // canvasCenter + panOffset = world point at screen centre
                                // centroid offset from canvas centre = centroid - canvasSize/2
                                val canvasCenter = Offset(size.width / 2f, size.height / 2f)
                                val centroidFromCenter = centroid - canvasCenter
                                panOffset = (panOffset - centroidFromCenter) * spanRatio + centroidFromCenter

                                zoomScale = newScale

                                // Commit an integer zoom step when scale drifts far enough
                                while (zoomScale >= 2f) {
                                    val newZoom = (currentZoom + 1).coerceIn(1, 19)
                                    panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                                    currentZoom = newZoom
                                    zoomScale /= 2f
                                }
                                while (zoomScale <= 0.5f) {
                                    val newZoom = (currentZoom - 1).coerceIn(1, 19)
                                    panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                                    currentZoom = newZoom
                                    zoomScale *= 2f
                                }
                                onZoomChanged(currentZoom)
                            }

                            lastCentroid = centroid
                            lastSpan = span
                        }
                    }
                }
            }
    ) {
        val canvasW = size.width
        val canvasH = size.height

        drawIntoCanvas { canvas ->
            canvas.withSave {
                // Scale around canvas centre for smooth continuous zoom
                canvas.translate(canvasW / 2f, canvasH / 2f)
                canvas.scale(zoomScale, zoomScale)
                canvas.translate(-canvasW / 2f, -canvasH / 2f)

                val (centerPxF, centerPyF) = TileMath.latLonToPixel(center.lat, center.lon, currentZoom)
                val centerPx = centerPxF - panOffset.x
                val centerPy = centerPyF - panOffset.y

                val originX = centerPx - canvasW / 2
                val originY = centerPy - canvasH / 2

                // When zoomScale < 1 the canvas transform shrinks tiles, so the
                // visible world area is larger than the canvas. Expand the tile
                // range so the extra area is filled.
                val invScale = 1f / zoomScale
                val extraW = canvasW * (invScale - 1f) / 2f
                val extraH = canvasH * (invScale - 1f) / 2f

                val tileX0 = ((originX - extraW) / tileSize).toInt() - 1
                val tileY0 = ((originY - extraH) / tileSize).toInt() - 1
                val tileX1 = ((originX + canvasW + extraW) / tileSize).toInt() + 1
                val tileY1 = ((originY + canvasH + extraH) / tileSize).toInt() + 1
                val maxTile = 2.0.pow(currentZoom).toInt() - 1

                for (tx in tileX0..tileX1) {
                    for (ty in tileY0..tileY1) {
                        if (tx < 0 || ty < 0 || tx > maxTile || ty > maxTile) continue

                        val key = Triple(tx, ty, currentZoom)
                        val bitmap = tiles[key]
                        if (bitmap == null) {
                            // BEFORE: scope.launch(...) was called every frame for every missing tile
                            // AFTER: only launch if not already being fetched
                            if (loadingTiles.add(key)) {
                                scope.launch(Dispatchers.Main) {
                                    loadTile(tx, ty, currentZoom)?.let { tiles[key] = it }
                                    loadingTiles.remove(key)
                                }
                            }
                        } else {
                            val drawX = (tx * tileSize - originX).toInt()
                            val drawY = (ty * tileSize - originY).toInt()
                            drawImage(
                                image = bitmap,
                                dstOffset = IntOffset(drawX, drawY),
                                dstSize = IntSize(tileSize, tileSize),
                            )
                        }
                    }
                }

                markers.forEach { marker ->
                    val (mpx, mpy) = TileMath.latLonToPixel(marker.position.lat, marker.position.lon, currentZoom)
                    val sx = (mpx - originX).toFloat()
                    val sy = (mpy - originY).toFloat()

                    // Draw the SVG marker pinned at its bottom-center
                    drawIntoCanvas { canvas ->
                        canvas.withSave {
                            // Translate so the pin tip aligns with the coordinate point
                            canvas.translate(sx - markerSize / 2f, sy - markerSize)
                            with(markerPainter) {
                                draw(
                                    size = androidx.compose.ui.geometry.Size(markerSize, markerSize)
                                )
                            }
                        }
                    }
                }
                polylines.forEach { polyline ->
                    val points = polyline.points.map { point ->
                        val (px, py) = TileMath.latLonToPixel(point.lat, point.lon, currentZoom)
                        Offset((px - originX).toFloat(), (py - originY).toFloat())
                    }
                    drawPoints(
                        points = points,
                        color = Color.Magenta,
                        pointMode = androidx.compose.ui.graphics.PointMode.Lines
                    )
                }
            }
        }
    }


}

/**
 * Rescales [panOffset] when changing zoom level so the currently visible
 * centre of the map stays in place instead of snapping back to [center].
 */
fun rescalePanOffset(
    panOffset: Offset,
    oldZoom: Int,
    newZoom: Int,
    center: LatLon,
): Offset {
    val scale = 2.0.pow(newZoom - oldZoom).toFloat()
    val (cx, cy) = TileMath.latLonToPixel(center.lat, center.lon, oldZoom)
    val (cxNew, cyNew) = TileMath.latLonToPixel(center.lat, center.lon, newZoom)

    val visibleCx = cx.toFloat() - panOffset.x
    val visibleCy = cy.toFloat() - panOffset.y

    return Offset(
        cxNew.toFloat() - visibleCx * scale,
        cyNew.toFloat() - visibleCy * scale,
    )
}